(() => {
  if (window.__redprintwareCartDrawerProLoadedV3) return;
  window.__redprintwareCartDrawerProLoadedV3 = true;

  const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
  const rootUrl = () => window.Shopify?.routes?.root || '/';
  let enhancementTimers = [];

  function renderCartSections(payload) {
    if (!payload?.sections) return;
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.renderContents) {
      drawer.renderContents(payload);
      return;
    }
    window.location.reload();
  }

  async function postCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        ...body,
        sections: sectionIds,
        sections_url: window.location.pathname,
      }),
    });

    const payload = await response.json();
    if (!response.ok || payload?.status) {
      throw new Error(payload?.description || payload?.message || 'Cart update failed.');
    }
    return payload;
  }

  function clearEnhancementTimers() {
    enhancementTimers.forEach((timer) => window.clearTimeout(timer));
    enhancementTimers = [];
  }

  function scheduleCartEnhancements() {
    // Refresh replaces #CartDrawer after AJAX add/update. Run once immediately
    // and again after the replacement/paint so the newly inserted timer always starts.
    clearEnhancementTimers();
    requestAnimationFrame(() => initCartTimers());
    [40, 120, 260].forEach((delay) => {
      enhancementTimers.push(window.setTimeout(initCartTimers, delay));
    });
  }

  if (!customElements.get('redprintware-cart-extras')) {
    customElements.define(
      'redprintware-cart-extras',
      class RedprintwareCartExtras extends HTMLElement {
        connectedCallback() {
          this.protection = {
            input: this.querySelector('[data-rpw-addon="protection"]'),
            variantId: this.dataset.protectionVariantId,
            lineKey: this.dataset.protectionLineKey,
            defaultOn: this.dataset.protectionDefault === 'true',
            storageKey: 'rpw-cart-addon-protection',
          };
          this.rescue = {
            input: this.querySelector('[data-rpw-addon="rescue"]'),
            variantId: this.dataset.rescueVariantId,
            lineKey: this.dataset.rescueLineKey,
            defaultOn: this.dataset.rescueDefault === 'true',
            storageKey: 'rpw-cart-addon-rescue',
          };

          this.discountInput = this.querySelector('[data-rpw-discount-input]');
          this.discountButton = this.querySelector('[data-rpw-discount-apply]');
          this.discountStatus = this.querySelector('[data-rpw-discount-status]');
          this.reconcilingDefaults = false;

          this.protection.input?.addEventListener('change', (event) => this.onAddonChange(this.protection, event));
          this.rescue.input?.addEventListener('change', (event) => this.onAddonChange(this.rescue, event));
          this.discountButton?.addEventListener('click', () => this.applyDiscount());
          this.discountInput?.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.applyDiscount();
            }
          });

          this.reconcileDefaultAddons();
        }

        get mainItemCount() {
          return Number.parseInt(this.dataset.mainItemCount || '0', 10) || 0;
        }

        userExplicitlyDisabled(addon) {
          return localStorage.getItem(addon.storageKey) === 'off';
        }

        setOptimisticDefaultState(addon, active) {
          if (!addon?.input) return;
          addon.input.checked = active;
          const row = addon.input.closest('.rpw-cart-addon');
          row?.classList.toggle('is-loading', active && !addon.lineKey);
          addon.input.disabled = active && !addon.lineKey;
        }

        async reconcileDefaultAddons() {
          if (this.reconcilingDefaults) return;
          this.reconcilingDefaults = true;

          const addons = [this.protection, this.rescue].filter((addon) => addon?.variantId);

          try {
            // If there are no normal cart items, remove stale paid add-ons.
            if (this.mainItemCount === 0) {
              const staleKeys = addons.map((addon) => addon.lineKey).filter(Boolean);
              if (!staleKeys.length) return;

              const updates = {};
              staleKeys.forEach((key) => (updates[key] = 0));
              const payload = await postCart(window.routes?.cart_update_url || `${rootUrl()}cart/update.js`, { updates });
              renderCartSections(payload);
              return;
            }

            const items = [];
            const optimistic = [];

            addons.forEach((addon) => {
              // A default-on add-on should ALWAYS be present unless the shopper
              // explicitly switched it off during this browsing session.
              if (!addon.defaultOn || addon.lineKey || this.userExplicitlyDisabled(addon)) return;

              this.setOptimisticDefaultState(addon, true);
              optimistic.push(addon);
              items.push({ id: Number(addon.variantId), quantity: 1 });
            });

            if (!items.length) return;

            const payload = await postCart(window.routes?.cart_add_url || `${rootUrl()}cart/add.js`, { items });
            renderCartSections(payload);
          } catch (error) {
            // Do not save an "on" preference before the server confirms it.
            // If auto-add fails, restore the visual state so it can retry next render/open.
            addons.forEach((addon) => {
              if (!addon.lineKey && !this.userExplicitlyDisabled(addon)) {
                this.setOptimisticDefaultState(addon, false);
                if (addon.input) addon.input.disabled = false;
                addon.input?.closest('.rpw-cart-addon')?.classList.remove('is-loading');
              }
            });
            console.error(error);
          } finally {
            this.reconcilingDefaults = false;
          }
        }

        async onAddonChange(addon, event) {
          const input = event.currentTarget;
          const checked = input.checked;
          const row = input.closest('.rpw-cart-addon');
          row?.classList.add('is-loading');
          input.disabled = true;

          try {
            let payload;
            if (checked) {
              payload = await postCart(window.routes?.cart_add_url || `${rootUrl()}cart/add.js`, {
                items: [{ id: Number(addon.variantId), quantity: 1 }],
              });
            } else if (addon.lineKey) {
              payload = await postCart(window.routes?.cart_change_url || `${rootUrl()}cart/change.js`, {
                id: addon.lineKey,
                quantity: 0,
              });
            } else {
              payload = await postCart(window.routes?.cart_update_url || `${rootUrl()}cart/update.js`, {
                updates: { [addon.variantId]: 0 },
              });
            }

            // Memory semantics requested:
            // - Default ON remains ON forever unless the shopper explicitly turns it OFF.
            // - Turning it back ON clears the OFF memory and restores default behavior.
            if (addon.defaultOn) {
              if (checked) localStorage.removeItem(addon.storageKey);
              else localStorage.setItem(addon.storageKey, 'off');
            } else if (checked) {
              localStorage.setItem(addon.storageKey, 'on');
            } else {
              localStorage.removeItem(addon.storageKey);
            }

            renderCartSections(payload);
          } catch (error) {
            input.checked = !checked;
            row?.classList.remove('is-loading');
            input.disabled = false;
            console.error(error);
          }
        }

        async applyDiscount() {
          const code = this.discountInput?.value.trim();
          if (!code || this.discountButton?.disabled) return;

          this.discountButton.disabled = true;
          this.discountStatus?.classList.remove('is-error');
          if (this.discountStatus) this.discountStatus.textContent = 'Applying…';

          try {
            const payload = await postCart(window.routes?.cart_update_url || `${rootUrl()}cart/update.js`, {
              discount: code,
            });
            renderCartSections(payload);
          } catch (error) {
            if (this.discountStatus) {
              this.discountStatus.textContent = error.message || 'Unable to apply this code.';
              this.discountStatus.classList.add('is-error');
            }
            this.discountButton.disabled = false;
          }
        }
      }
    );
  }

  function initCartTimers() {
    document.querySelectorAll('[data-rpw-cart-timer]').forEach((node) => {
      if (node.dataset.timerStarted === 'true') return;
      node.dataset.timerStarted = 'true';

      const minutes = Math.max(1, Number.parseInt(node.dataset.minutes || '10', 10) || 10);
      const storageKey = `rpw-cart-offer-expiry-${minutes}`;
      let expiry = Number(sessionStorage.getItem(storageKey));

      if (!expiry || expiry <= Date.now()) {
        expiry = Date.now() + minutes * 60_000;
        sessionStorage.setItem(storageKey, String(expiry));
      }

      const tick = () => {
        if (!node.isConnected) return;
        const remaining = Math.max(0, expiry - Date.now());
        const totalSeconds = Math.floor(remaining / 1000);
        const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
        const ss = String(totalSeconds % 60).padStart(2, '0');
        node.textContent = `${mm}:${ss}`;
        node.classList.toggle('is-expired', remaining <= 0);

        if (remaining > 0) window.setTimeout(tick, 250);
      };

      tick();
    });
  }

  // Hook directly into Refresh's CartDrawer render lifecycle. Product-form.js
  // renders/replaces the drawer AFTER the cartUpdate publication, which is why
  // the V2 timer could miss the new node when Add to cart was the trigger.
  customElements.whenDefined('cart-drawer').then(() => {
    const DrawerClass = customElements.get('cart-drawer');
    if (!DrawerClass || DrawerClass.prototype.__dlV3Patched) return;

    const originalRenderContents = DrawerClass.prototype.renderContents;
    const originalOpen = DrawerClass.prototype.open;

    if (typeof originalRenderContents === 'function') {
      DrawerClass.prototype.renderContents = function (...args) {
        const result = originalRenderContents.apply(this, args);
        scheduleCartEnhancements();
        return result;
      };
    }

    if (typeof originalOpen === 'function') {
      DrawerClass.prototype.open = function (...args) {
        const result = originalOpen.apply(this, args);
        scheduleCartEnhancements();
        return result;
      };
    }

    DrawerClass.prototype.__dlV3Patched = true;
  });

  document.addEventListener('DOMContentLoaded', scheduleCartEnhancements);
  document.addEventListener('click', (event) => {
    if (event.target.closest('#cart-icon-bubble')) scheduleCartEnhancements();
  });

  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleCartEnhancements);
  }
})();
